import { useT } from "../../../shared/i18n/i18n";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import type { Editor } from "@tiptap/react";
import { getMarkRange, useEditorState } from "@tiptap/react";
import { ExternalLink, Pencil, Unlink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Clicking into a link raises a small popover with the URL and open / edit /
// remove actions — the mnemos link-popover mechanic on our own floating.
export function LinkPopover({ editor }: { editor: Editor }) {
  const t = useT();
  const [isEditing, setIsEditing] = useState(false);
  const [draftHref, setDraftHref] = useState("");

  const linkState = useEditorState({
    editor,
    selector: (context) => {
      const instance = context.editor;
      if (!instance) return null;
      const { selection } = instance.state;
      if (!selection.empty || !instance.isActive("link")) return null;
      const linkType = instance.schema.marks.link;
      if (!linkType) return null;
      const range = getMarkRange(instance.state.doc.resolve(selection.from), linkType);
      if (!range) return null;
      const href = (instance.getAttributes("link").href as string | undefined) ?? "";
      return { from: range.from, href, to: range.to };
    },
  });

  const floating = useFloating({
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    open: linkState !== null,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  });
  const floatingRef = useRef(floating);
  floatingRef.current = floating;

  useEffect(() => {
    setIsEditing(false);
    setDraftHref(linkState?.href ?? "");
  }, [linkState?.from, linkState?.href]);

  useEffect(() => {
    if (!linkState) return;
    const from = linkState.from;
    floatingRef.current.refs.setReference({
      contextElement: editor.view.dom,
      getBoundingClientRect: () => {
        try {
          const start = editor.view.coordsAtPos(from);
          const end = editor.view.coordsAtPos(linkState.to);
          return new DOMRect(start.left, start.top, Math.max(end.right - start.left, 4), start.bottom - start.top);
        } catch {
          return new DOMRect(-9999, -9999, 0, 0);
        }
      },
    });
    floatingRef.current.update();
  }, [editor, linkState]);

  if (!linkState || typeof document === "undefined") return null;

  function applyDraft() {
    const href = draftHref.trim();
    const range = { from: linkState!.from, to: linkState!.to };
    if (href.length === 0) {
      editor.chain().focus().setTextSelection(range).unsetLink().run();
    } else {
      const normalized = /^(https?:|mailto:)/i.test(href) ? href : `https://${href}`;
      editor.chain().focus().setTextSelection(range).setLink({ href: normalized }).run();
    }
    setIsEditing(false);
  }

  return createPortal(
    <div
      className="z-50 flex items-center gap-1 rounded-md border border-border/70 bg-popover p-1 shadow-md"
      data-editor-chrome
      ref={floating.refs.setFloating}
      style={{ ...floating.floatingStyles }}
    >
      {isEditing ? (
        <>
          <Input
            autoFocus
            className="h-7 w-56 text-xs"
            onChange={(event) => setDraftHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyDraft();
              }
              if (event.key === "Escape") {
                // Cancel only the link editor, not the surrounding dialog.
                event.preventDefault();
                event.stopPropagation();
                setIsEditing(false);
              }
            }}
            value={draftHref}
          />
          <Button onClick={applyDraft} size="sm" variant="secondary">
            {t("editor.set")}
          </Button>
        </>
      ) : (
        <>
          <a
            className="max-w-64 truncate px-1.5 text-xs text-primary underline underline-offset-2"
            href={linkState.href}
            rel="noreferrer"
            target="_blank"
          >
            {linkState.href}
          </a>
          <Button
            aria-label={t("editor.openLink")}
            className="size-6 text-muted-foreground [&_svg]:size-3"
            onClick={() => window.open(linkState.href, "_blank", "noopener,noreferrer")}
            size="icon-xs"
            title={t("editor.openLink")}
            variant="ghost"
          >
            <ExternalLink />
          </Button>
          <Button
            aria-label={t("editor.editLink")}
            className="size-6 text-muted-foreground [&_svg]:size-3"
            onClick={() => setIsEditing(true)}
            size="icon-xs"
            title={t("editor.editLink")}
            variant="ghost"
          >
            <Pencil />
          </Button>
          <Button
            aria-label={t("editor.removeLink")}
            className="size-6 text-muted-foreground [&_svg]:size-3"
            onClick={() =>
              editor.chain().focus().setTextSelection({ from: linkState.from, to: linkState.to }).unsetLink().run()
            }
            size="icon-xs"
            title={t("editor.removeLink")}
            variant="ghost"
          >
            <Unlink />
          </Button>
        </>
      )}
    </div>,
    document.body,
  );
}
